import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import { apiClient } from "../api/client";
import { ServiceSession, SessionDashboardResponse } from "../api/PartnerPortalGatewayApi";

export type RefreshInterval = 60000 | 180000 | 300000; // 1m, 3m, 5m
export type SessionType = "previous" | "current" | "upcoming";

interface UseSessionDashboardResult {
  sessions: ServiceSession[];
  selectedSession: ServiceSession | null;
  selectedSessionType: SessionType | null;
  currentSessionIndex: number;
  dashboardData: SessionDashboardResponse | null;
  isLoadingSessions: boolean;
  isLoadingDashboard: boolean;
  sessionsError: Error | null;
  dashboardError: Error | null;
  refreshInterval: RefreshInterval;
  setRefreshInterval: (interval: RefreshInterval) => void;
  selectSession: (session: ServiceSession) => void;
  selectPreviousSession: () => void;
  selectNextSession: () => void;
  hasPreviousSession: boolean;
  hasNextSession: boolean;
  refetch: () => void;
}

const getSessionType = (session: ServiceSession): SessionType => {
  const now = dayjs();
  const sessionStart = session.sessionStartServerTime;
  const sessionEnd = session.sessionEndServerTime;

  if (now.isAfter(sessionEnd)) {
    return "previous";
  } else if (now.isBefore(sessionStart)) {
    return "upcoming";
  } else {
    return "current";
  }
};

const getDefaultSessionIndex = (sessions: ServiceSession[]): number => {
  if (sessions.length === 0) return -1;

  // Find current session first
  const currentIndex = sessions.findIndex((session) => getSessionType(session) === "current");
  if (currentIndex >= 0) return currentIndex;

  // Find first upcoming session
  const upcomingIndex = sessions.findIndex((session) => getSessionType(session) === "upcoming");
  if (upcomingIndex >= 0) return upcomingIndex;

  // Otherwise return the last previous session
  const previousIndices = sessions
    .map((session, index) => (getSessionType(session) === "previous" ? index : -1))
    .filter((i) => i >= 0);
  if (previousIndices.length > 0) {
    return previousIndices[previousIndices.length - 1];
  }

  return 0;
};

export const useSessionDashboard = (serviceId: number | null): UseSessionDashboardResult => {
  const queryClient = useQueryClient();
  const [currentSessionIndex, setCurrentSessionIndex] = useState<number>(-1);
  const [refreshInterval, setRefreshInterval] = useState<RefreshInterval>(60000);
  const [refreshKey, setRefreshKey] = useState(0);
  const [timeKey, setTimeKey] = useState(0); // Key to force recalculation of session types
  const previousDataRef = useRef<SessionDashboardResponse | null>(null);

  // Fetch sessions around current time (yesterday to tomorrow)
  const {
    data: sessionsRaw = [],
    isLoading: isLoadingSessions,
    error: sessionsError,
    refetch: refetchSessions,
  } = useQuery({
    queryKey: ["serviceSessions", serviceId],
    queryFn: async (): Promise<ServiceSession[]> => {
      if (!serviceId) return [];

      const result = await apiClient.service.getServiceSessionsAroundNow(serviceId);
      console.log("Sessions loaded:", result);
      return result;
    },
    enabled: !!serviceId,
  });

  // Sessions are already ordered from the API
  const sessions = sessionsRaw;

  // Reset index when service changes
  useEffect(() => {
    setCurrentSessionIndex(-1);
  }, [serviceId]);

  // Set default session index when sessions load
  useEffect(() => {
    if (sessions.length > 0 && currentSessionIndex === -1) {
      const defaultIndex = getDefaultSessionIndex(sessions);
      console.log("Setting default session index:", defaultIndex, "sessionId:", sessions[defaultIndex]?.sessionId);
      setCurrentSessionIndex(defaultIndex);
    }
  }, [sessions, currentSessionIndex]);

  // Get selected session from index
  const selectedSession = useMemo(() => {
    if (currentSessionIndex < 0 || currentSessionIndex >= sessions.length) return null;
    return sessions[currentSessionIndex];
  }, [sessions, currentSessionIndex]);

  const selectedSessionId = selectedSession?.sessionId ?? null;
  const selectedRollId = selectedSession?.rollId ?? null;

  // Get the type of the selected session (recalculated when timeKey changes)
  const selectedSessionType = useMemo<SessionType | null>(() => {
    if (!selectedSession) return null;
    return getSessionType(selectedSession);
  }, [selectedSession, timeKey]);

  // Navigation state - simple boolean checks
  const hasPreviousSession = currentSessionIndex > 0;
  const hasNextSession = currentSessionIndex >= 0 && currentSessionIndex < sessions.length - 1;

  // Fetch dashboard data for selected session
  const {
    data: dashboardData,
    isLoading: isLoadingDashboard,
    isFetching: _isFetchingDashboard,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useQuery({
    queryKey: ["sessionDashboard", serviceId, selectedSessionId, selectedRollId, refreshKey],
    queryFn: async (): Promise<SessionDashboardResponse | null> => {
      if (!serviceId || !selectedSessionId || !selectedRollId) return null;

      console.log("Loading dashboard for sessionId:", selectedSessionId, "rollId:", selectedRollId);
      return await apiClient.dashboard.getSessionDashboard(serviceId, selectedSessionId, selectedRollId);
    },
    enabled: !!serviceId && !!selectedSessionId && !!selectedRollId,
    refetchOnWindowFocus: false,
  });

  // Track data changes for logging
  useEffect(() => {
    if (dashboardData) {
      previousDataRef.current = dashboardData;
    }
  }, [dashboardData]);

  // Background polling - refetch data in the background without blocking UI
  useEffect(() => {
    if (!serviceId || !selectedSessionId || !selectedRollId) return;

    const intervalId = setInterval(async () => {
      const timestamp = new Date().toISOString();
      console.log(`[${timestamp}] Background refresh triggered for session ${selectedSessionId}`);

      // Update timeKey to recalculate session type based on current time
      setTimeKey((k) => k + 1);

      try {
        const result = await refetchDashboard();
        const newData = result.data;

        console.log(`[${timestamp}] Background refresh completed`);
        console.log("New data received:", newData);

        // Compare with previous data
        if (previousDataRef.current) {
          const hasChanges = JSON.stringify(previousDataRef.current) !== JSON.stringify(newData);
          if (hasChanges) {
            console.log(`[${timestamp}] ✅ Data changed - differences detected`);
            console.log("Previous child count:", previousDataRef.current?.children?.length);
            console.log("New child count:", newData?.children?.length);

            // Log specific changes in children
            const prevChildren = previousDataRef.current?.children || [];
            const newChildren = newData?.children || [];

            if (prevChildren.length !== newChildren.length) {
              console.log("Child count changed:", prevChildren.length, "→", newChildren.length);
            }

            // Check for status changes
            const statusChanges = newChildren.filter((newChild, _idx) => {
              const prevChild = prevChildren.find((pc) => pc.childId === newChild.childId);
              return prevChild && prevChild.eventType !== newChild.eventType;
            });

            if (statusChanges.length > 0) {
              console.log(
                `${statusChanges.length} children status changes:`,
                statusChanges.map((c) => ({
                  name: `${c.firstName} ${c.lastName}`,
                  childId: c.childId,
                  newStatus: c.eventType,
                })),
              );
            }
          } else {
            console.log(`[${timestamp}] ⚪ No changes detected - data is identical`);
          }
        } else {
          console.log(`[${timestamp}] First data load`);
        }

        previousDataRef.current = newData ?? null;
      } catch (error) {
        console.error(`[${timestamp}] ❌ Background refresh failed:`, error);
      }
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [serviceId, selectedSessionId, selectedRollId, refreshInterval, refetchDashboard]);

  // Navigation functions
  const selectPreviousSession = useCallback(() => {
    setCurrentSessionIndex((prev) => {
      if (prev > 0) {
        const newIndex = prev - 1;
        console.log("Navigating to previous, new index:", newIndex);
        setRefreshKey((k) => k + 1);
        return newIndex;
      }
      return prev;
    });
  }, []);

  const selectNextSession = useCallback(() => {
    setCurrentSessionIndex((prev) => {
      if (prev >= 0 && prev < sessions.length - 1) {
        const newIndex = prev + 1;
        console.log("Navigating to next, new index:", newIndex);
        setRefreshKey((k) => k + 1);
        return newIndex;
      }
      return prev;
    });
  }, [sessions.length]);

  const selectSession = useCallback(
    (session: ServiceSession) => {
      const index = sessions.findIndex((s) => s.sessionId === session.sessionId);
      if (index >= 0) {
        console.log("Selecting session at index:", index, "sessionId:", session.sessionId);
        setCurrentSessionIndex(index);
        setRefreshKey((k) => k + 1);
        queryClient.invalidateQueries({ queryKey: ["sessionDashboard", serviceId, session.sessionId] });
      }
    },
    [sessions, queryClient, serviceId],
  );

  const refetch = useCallback(() => {
    refetchSessions();
    refetchDashboard();
  }, [refetchSessions, refetchDashboard]);

  return {
    sessions,
    selectedSession,
    selectedSessionType,
    currentSessionIndex,
    dashboardData: dashboardData ?? null,
    isLoadingSessions,
    isLoadingDashboard,
    sessionsError: sessionsError as Error | null,
    dashboardError: dashboardError as Error | null,
    refreshInterval,
    setRefreshInterval,
    selectSession,
    selectPreviousSession,
    selectNextSession,
    hasPreviousSession,
    hasNextSession,
    refetch,
  };
};
